/**
 * PendientesMontar
 * ----------------
 * Vista admin · "📦 Pendientes de montar".
 *
 * Lista todos los componentes que están listos (recibido_almacen / aprobado / producido)
 * pero AÚN no asignados a ninguna parada de ruta. Agrupados por espacio (PDV) para
 * facilitar el "voy a hacer una ruta a Sevilla mañana, ¿qué tengo listo para llevar?".
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  MapPin, Package, Loader2, Filter, RefreshCcw, Calendar,
  Building2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

export default function PendientesMontar() {
  const [loading, setLoading] = useState(true);
  const [grupos, setGrupos] = useState([]);
  const [filtroEspacio, setFiltroEspacio] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");

  /* eslint-disable */
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/componentes/pendientes-montar");
      setGrupos(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  /* eslint-enable */

  const filtrados = useMemo(() => {
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const qE = norm(filtroEspacio);
    const qC = norm(filtroCliente);
    return grupos.filter((g) => {
      if (qE && !norm(g.espacio_nombre).includes(qE) && !norm(g.espacio_ciudad).includes(qE)) return false;
      if (qC && !norm(g.cliente_nombre).includes(qC)) return false;
      return true;
    });
  }, [grupos, filtroEspacio, filtroCliente]);

  // Iter 58 — clasificación por urgencia (más antiguo en taller = más urgente)
  const conUrgencia = useMemo(() => {
    const ahora = new Date();
    return filtrados.map((g) => {
      const fechasEntrada = g.componentes
        .map((c) => c.fecha_entrada || c.fecha_recepcion)
        .filter(Boolean);
      let diasMax = 0;
      if (fechasEntrada.length > 0) {
        const oldest = new Date(Math.min(...fechasEntrada.map((f) => new Date(f).getTime())));
        diasMax = Math.floor((ahora - oldest) / (1000 * 60 * 60 * 24));
      }
      const urgencia = diasMax >= 7 ? "alta" : diasMax >= 3 ? "media" : "baja";
      return { ...g, diasMax, urgencia };
    });
  }, [filtrados]);

  const grupoUrgencia = useMemo(() => {
    return {
      alta: conUrgencia.filter((g) => g.urgencia === "alta"),
      media: conUrgencia.filter((g) => g.urgencia === "media"),
      baja: conUrgencia.filter((g) => g.urgencia === "baja"),
    };
  }, [conUrgencia]);

  const [vistaKanban, setVistaKanban] = useState(false);

  const totalComps = useMemo(
    () => filtrados.reduce((acc, g) => acc + (g.componentes?.length || 0), 0),
    [filtrados]
  );

  return (
    <div className="space-y-4" data-testid="pendientes-montar-page">
      <PageHeader
        title="📦 Pendientes de montar"
        subtitle={`Componentes listos en taller/nave sin asignar a ruta · agrupados por PDV`}
        action={
          <div className="flex gap-2">
            <Button
              variant={vistaKanban ? "default" : "outline"}
              onClick={() => setVistaKanban((v) => !v)}
              data-testid="btn-toggle-kanban"
            >
              {vistaKanban ? "📋 Vista lista" : "🗂 Vista urgencia"}
            </Button>
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refrescar
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Espacios con material pendiente</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">{filtrados.length}</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Componentes totales sin asignar</div>
          <div className="text-2xl font-bold text-emerald-300 mt-1">{totalComps}</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Filtrar por espacio / ciudad…"
            value={filtroEspacio}
            onChange={(e) => setFiltroEspacio(e.target.value)}
            className="bg-zinc-950 border-zinc-800 text-xs flex-1"
            data-testid="filtro-espacio-input"
          />
          <Input
            placeholder="Cliente…"
            value={filtroCliente}
            onChange={(e) => setFiltroCliente(e.target.value)}
            className="bg-zinc-950 border-zinc-800 text-xs w-32"
            data-testid="filtro-cliente-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando pendientes…
        </div>
      ) : filtrados.length === 0 ? (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-12 text-center text-zinc-500">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No hay componentes pendientes de montar.</p>
          <p className="text-xs mt-1">Cuando recibas material, aparecerá aquí agrupado por PDV.</p>
        </div>
      ) : vistaKanban ? (
        /* Iter 58 · Vista kanban por urgencia */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="kanban-urgencia">
          {[
            { key: "alta", label: "🔴 Urgente (>7d en taller)", color: "border-red-500/30 bg-red-500/5" },
            { key: "media", label: "🟡 Próximo (3-7d)", color: "border-amber-500/30 bg-amber-500/5" },
            { key: "baja", label: "🟢 Reciente (<3d)", color: "border-emerald-500/30 bg-emerald-500/5" },
          ].map((col) => (
            <div key={col.key} className={`rounded-xl border ${col.color} p-3`} data-testid={`kanban-col-${col.key}`}>
              <div className="font-semibold text-sm text-zinc-100 mb-3 flex items-center justify-between">
                <span>{col.label}</span>
                <Badge variant="outline" className="text-[10px]">{grupoUrgencia[col.key].length}</Badge>
              </div>
              <div className="space-y-2">
                {grupoUrgencia[col.key].length === 0 && (
                  <div className="text-center text-zinc-600 text-xs py-6">— vacío —</div>
                )}
                {grupoUrgencia[col.key].map((g) => (
                  <div key={g.espacio_id} className="bg-zinc-950/80 border border-zinc-800 rounded-lg p-2.5 text-xs">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-zinc-100 truncate">{g.espacio_nombre}</div>
                        {g.cliente_nombre && <div className="text-[10px] text-zinc-500 truncate">{g.cliente_nombre}</div>}
                      </div>
                      <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] shrink-0" variant="outline">
                        {g.componentes.length}
                      </Badge>
                    </div>
                    {g.diasMax > 0 && (
                      <div className="text-[10px] text-zinc-500 mb-1.5">📅 {g.diasMax} día(s) en taller</div>
                    )}
                    <div className="text-[10px] text-zinc-400 truncate">
                      {g.componentes.slice(0, 2).map((c) => c.nombre).join(", ")}
                      {g.componentes.length > 2 && ` +${g.componentes.length - 2} más`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filtrados.map((g) => (
            <div key={g.espacio_id || "_sin_espacio"} className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="bg-zinc-950 px-4 py-3 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-400" />
                    <h3 className="font-semibold text-zinc-100">{g.espacio_nombre}</h3>
                    {g.cliente_nombre && (
                      <Badge variant="outline" className="text-[10px] text-cyan-300 border-cyan-500/30">
                        <Building2 className="w-3 h-3 mr-1" />
                        {g.cliente_nombre}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-3">
                    {g.espacio_direccion && <span>{g.espacio_direccion}</span>}
                    {g.espacio_ciudad && <span>· {g.espacio_ciudad}</span>}
                    {g.espacio_provincia && <span>({g.espacio_provincia})</span>}
                  </div>
                </div>
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30" variant="outline">
                  {g.componentes.length} pendiente(s)
                </Badge>
              </div>
              <div className="px-4 py-3">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                    <tr>
                      <th className="text-left py-1">Componente</th>
                      <th className="text-left py-1">Petición</th>
                      <th className="text-left py-1">Estado</th>
                      <th className="text-left py-1">Entrada</th>
                      <th className="text-right py-1"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/40">
                    {g.componentes.map((c) => (
                      <tr key={c.id} data-testid={`pendiente-row-${c.id}`}>
                        <td className="py-2">
                          <div className="text-zinc-100">{c.nombre}</div>
                          <div className="text-[11px] text-zinc-500">
                            {c.cantidad > 1 && <span>×{c.cantidad} </span>}
                            {c.ancho_cm && c.alto_cm && <span>· {c.ancho_cm}×{c.alto_cm}cm</span>}
                            {c.bulto_numero && <span> · 📦 Bulto {c.bulto_numero}</span>}
                          </div>
                        </td>
                        <td className="py-2 text-xs">
                          <Link to={`/peticiones/${c._peticion_id}`} className="text-amber-400 hover:text-amber-300 font-mono">
                            {c._peticion_codigo || "—"}
                          </Link>
                          <div className="text-[10px] text-zinc-500 truncate max-w-[180px]">{c._peticion_nombre}</div>
                        </td>
                        <td className="py-2 text-xs">
                          <Badge variant="outline" className="text-[10px]">{c.estado}</Badge>
                        </td>
                        <td className="py-2 text-xs text-zinc-400">
                          {c.fecha_entrada ? (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {c.fecha_entrada}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="py-2 text-right">
                          <Link to={`/peticiones/${c._peticion_id}`}>
                            <Button size="sm" variant="ghost" className="h-7 text-xs">
                              <ExternalLink className="w-3 h-3 mr-1" /> Abrir
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
